import CompactDrawerContentHeader from '@/components/Drawer/CompactDrawerContentHeader'
import CompactDrawerCurrentProfileCard from '@/components/Drawer/CompactDrawerCurrentProfileCard'
import { ColorEnum } from '@/constants/color'
import { getFocusedRouteResetPath } from '@/features/navigation/drawer/getFocusedRouteResetPath'
import { RenderedDrawerRoute } from '@/features/navigation/drawer/types'
import { TileMenuItem } from '@/features/ui/navigation'
import { useScreenWidth } from '@/hooks/screen'
import { DrawerContentComponentProps } from '@react-navigation/drawer'
import { CommonActions, DrawerActions } from '@react-navigation/native'
import { usePathname, useRouter } from 'expo-router'
import { FC, useCallback } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

const CompactDrawerContent: FC<
    DrawerContentComponentProps & { renderedRoutes: RenderedDrawerRoute[] }
> = ({ state, navigation, renderedRoutes }) => {
    const screenWidth = useScreenWidth()
    const insets = useSafeAreaInsets()
    const pathname = usePathname()
    const router = useRouter()

    const isFocused = useCallback(
        (name: string) => state.routeNames[state.index] === name,
        [state.index, state.routeNames]
    )

    const availableWidth = screenWidth - insets.left - insets.right
    const itemFlexBasis = availableWidth / 2 - gap / 2

    return (
        <View style={styles.container}>
            <CompactDrawerContentHeader />
            <CompactDrawerCurrentProfileCard />
            <ScrollView
                contentContainerStyle={[
                    styles.drawerItemsContentContainer,
                    {
                        paddingBottom: 32 + insets.bottom,
                        paddingLeft: insets.left,
                        paddingRight: insets.right,
                    },
                ]}
                style={styles.drawerItemsContainer}
            >
                {renderedRoutes.map((route, index) => {
                    const focused = isFocused(route.name)

                    const handlePress = () => {
                        const resetPath = getFocusedRouteResetPath(route, pathname, focused)

                        if (resetPath) router.dismissTo(resetPath)

                        navigation.dispatch({
                            ...(focused
                                ? DrawerActions.closeDrawer()
                                : CommonActions.navigate(route.name)),
                            target: state.key,
                        })
                    }

                    return (
                        <View
                            key={route.name}
                            style={{
                                flexBasis: itemFlexBasis,
                                alignItems: index % 2 !== 0 ? 'flex-start' : 'flex-end',
                            }}
                        >
                            <TileMenuItem
                                Icon={route.Icon}
                                label={route.title}
                                onPress={handlePress}
                                Badge={route.Badge}
                                color={route.color}
                            />
                        </View>
                    )
                })}
            </ScrollView>
        </View>
    )
}

const gap = 32

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: 'center',
        backgroundColor: ColorEnum.JET_900,
    },
    drawerItemsContentContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap,
    },
    drawerItemsContainer: {
        alignSelf: 'flex-start',
        width: '100%',
        marginTop: 32,
    },
})

export default CompactDrawerContent
